import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuthState } from 'react-firebase-hooks/auth';
import { signInWithEmailAndPassword } from 'firebase/auth';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Divider,
  Grid,
  Link,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import FirebaseAuth from '../services/FirebaseAuth';
import SignInWithButton, {
  SignInProviders,
} from '../components/SignInWithButton';
import './OAuthAuthorizeRoute.css';

const REQUIRED_PARAMS = [
  'response_type',
  'client_id',
  'redirect_uri',
  'code_challenge',
] as const;

interface ClientInfo {
  client_id: string;
  client_name: string;
  client_uri?: string;
  logo_uri?: string;
  redirect_uris: string[];
  scope?: string;
}

function OAuthAuthorizeRoute() {
  const [searchParams] = useSearchParams();
  const [user, authLoading] = useAuthState(FirebaseAuth);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [signingIn, setSigningIn] = useState(false);
  const [authorizing, setAuthorizing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [clientInfo, setClientInfo] = useState<ClientInfo | null>(null);
  const [clientInfoLoading, setClientInfoLoading] = useState(true);

  const oauthParams = useMemo(
    () => ({
      response_type: searchParams.get('response_type') ?? '',
      client_id: searchParams.get('client_id') ?? '',
      redirect_uri: searchParams.get('redirect_uri') ?? '',
      state: searchParams.get('state') ?? '',
      code_challenge: searchParams.get('code_challenge') ?? '',
      code_challenge_method: searchParams.get('code_challenge_method') ?? 'S256',
      scope: searchParams.get('scope') ?? 'mcp',
      resource: searchParams.get('resource') ?? '',
    }),
    [searchParams]
  );

  const paramsValid = REQUIRED_PARAMS.every(
    (p) => oauthParams[p as keyof typeof oauthParams]
  );

  // Load client metadata for the consent screen
  useEffect(() => {
    if (!paramsValid) {
      setClientInfoLoading(false);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(
          `/oauth/client_info?client_id=${encodeURIComponent(oauthParams.client_id)}`
        );
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = (await res.json()) as ClientInfo;
        if (!cancelled) setClientInfo(data);
      } catch {
        if (!cancelled) {
          setClientInfo({
            client_id: oauthParams.client_id,
            client_name: oauthParams.client_id,
            redirect_uris: [oauthParams.redirect_uri],
          });
        }
      } finally {
        if (!cancelled) setClientInfoLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [paramsValid, oauthParams.client_id, oauthParams.redirect_uri]);

  const submitAuthorization = useCallback(
    async (idToken: string) => {
      setAuthorizing(true);
      setError(null);
      try {
        const res = await fetch('/oauth/authorize', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id_token: idToken, ...oauthParams }),
        });
        if (!res.ok) {
          const data = (await res.json().catch(() => ({}))) as {
            error_description?: string;
            error?: string;
          };
          throw new Error(
            data.error_description ?? data.error ?? `HTTP ${res.status}`
          );
        }
        const data = (await res.json()) as { redirect_url: string };
        window.location.href = data.redirect_url;
      } catch (err: unknown) {
        setError(err instanceof Error ? err.message : 'Authorization failed');
        setAuthorizing(false);
      }
    },
    [oauthParams]
  );

  const handleApprove = async () => {
    if (!user) return;
    const idToken = await user.getIdToken();
    await submitAuthorization(idToken);
  };

  const handleDeny = () => {
    // RFC 6749 §4.1.2.1 — redirect back with access_denied
    try {
      const url = new URL(oauthParams.redirect_uri);
      url.searchParams.set('error', 'access_denied');
      url.searchParams.set(
        'error_description',
        'User denied the authorization request'
      );
      if (oauthParams.state) url.searchParams.set('state', oauthParams.state);
      window.location.href = url.toString();
    } catch {
      setError('Invalid redirect_uri');
    }
  };

  const handleSignIn = async (e: React.FormEvent) => {
    e.preventDefault();
    setSigningIn(true);
    setError(null);
    try {
      await signInWithEmailAndPassword(FirebaseAuth, email, password);
      // After sign-in, the consent screen will render (user becomes truthy).
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Sign in failed');
    } finally {
      setSigningIn(false);
    }
  };

  if (!paramsValid) {
    return (
      <Box className="OAuthAuthorizeRoute">
        <Alert severity="error">Invalid or missing OAuth parameters.</Alert>
      </Box>
    );
  }

  if (authLoading || clientInfoLoading) {
    return (
      <Box className="OAuthAuthorizeRoute OAuthAuthorizeRoute--loading">
        <CircularProgress />
      </Box>
    );
  }

  // ─── Signed-out: sign-in form ──────────────────────────────────────────────
  if (!user) {
    return (
      <Box className="OAuthAuthorizeRoute">
        <Typography variant="h6" sx={{ mb: 1 }}>
          Sign in to continue
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          <strong>{clientInfo?.client_name}</strong> wants to access your
          account.
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <form onSubmit={handleSignIn}>
          <TextField
            label="Email"
            type="email"
            fullWidth
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            sx={{ mb: 2 }}
            disabled={signingIn}
          />
          <TextField
            label="Password"
            type="password"
            fullWidth
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            sx={{ mb: 3 }}
            disabled={signingIn}
          />
          <Button
            type="submit"
            variant="contained"
            fullWidth
            disabled={signingIn}
          >
            {signingIn ? <CircularProgress size={20} /> : 'Sign in'}
          </Button>
        </form>

        <Divider sx={{ my: 3 }}>or</Divider>

        <Grid container textAlign="center" justifyContent="center">
          {Object.keys(SignInProviders).map((p) => (
            <Grid item xs key={`oauth_signin_${p}`}>
              <SignInWithButton
                provider={p as SignInProviders}
                errorHandler={(err: { message?: string; code?: string }) =>
                  setError(err?.message ?? err?.code ?? 'Sign-in failed')
                }
              />
            </Grid>
          ))}
        </Grid>
      </Box>
    );
  }

  // ─── Signed-in: consent screen ────────────────────────────────────────────
  return (
    <Box className="OAuthAuthorizeRoute">
      <Typography variant="h6" sx={{ mb: 1 }}>
        Authorize access
      </Typography>

      <Stack spacing={1} sx={{ mb: 3 }}>
        <Typography variant="body2">
          <strong>{clientInfo?.client_name}</strong> is requesting permission to
          access your account.
        </Typography>
        {clientInfo?.client_uri && (
          <Typography variant="body2" color="text.secondary">
            <Link href={clientInfo.client_uri} target="_blank" rel="noopener">
              {clientInfo.client_uri}
            </Link>
          </Typography>
        )}
        <Typography variant="body2" color="text.secondary">
          Signed in as <strong>{user.email}</strong>
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Redirect URI:{' '}
          <code style={{ wordBreak: 'break-all' }}>
            {oauthParams.redirect_uri}
          </code>
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Permissions:{' '}
          {oauthParams.scope === 'mcp'
            ? 'read, create, edit and delete your invoices, customers, services, expenses and settings'
            : <code>{oauthParams.scope}</code>}
        </Typography>
      </Stack>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Stack direction="row" spacing={2}>
        <Button
          variant="outlined"
          fullWidth
          onClick={handleDeny}
          disabled={authorizing}
        >
          Deny
        </Button>
        <Button
          variant="contained"
          fullWidth
          onClick={handleApprove}
          disabled={authorizing}
        >
          {authorizing ? <CircularProgress size={20} /> : 'Approve'}
        </Button>
      </Stack>

      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ display: 'block', mt: 3, textAlign: 'center' }}
      >
        Not you?{' '}
        <Link component="button" onClick={() => FirebaseAuth.signOut()}>
          Sign in with a different account
        </Link>
      </Typography>
    </Box>
  );
}

export default OAuthAuthorizeRoute;
